import React, { useCallback, useMemo, useState } from 'react'
import { Col, Container, Row, Form } from 'react-bootstrap'
import { SelectOption } from '../../../../../../components/SelectDropdown/SelectDropdown'
import { Tooltip } from '../../../../../../components/Tooltip/Tooltip'
import { Button } from '../../../../../../components/Button/Button'
import { ConditionRow } from '../../../../../../components/ConditionRow/ConditionRow'
import { DescriptorType, Filter, FilterExpression, FilterOperator, GroupOperator, OPERATOR_OPTIONS } from '../../../../../../models/Account'
import { FormFieldDefinition, IntegrationObjectRelationship } from '../../hooks'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useGetDescribeQuery } from '../../../../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../../../../common/integrations'

const FILTER_VALUE_VARIABLES = ['currentUser', 'currentUserName']

interface FilterCondition {
  id: string
  reference: string | null
  operator: FilterOperator | null
  value: string
  enforced?: boolean
}

export interface IntegrationMappingFilterBuilderProps {
  serviceId: IntegrationServiceId
  value?: Filter[]
  onChange: (value: Filter[]) => void
  availableFormFieldDefinitions: FormFieldDefinition[]
  availableRelationships?: IntegrationObjectRelationship[]
  loading?: boolean
  placeholders: FilterExpression[]
}

const mapValueToConditions = (inputValues: Filter[] | undefined): FilterCondition[] => {
  if (!inputValues || inputValues.length === 0) {
    return []
  }
  const firstGroup = inputValues[0]
  return (firstGroup.expressions || []).map((exp) => ({
    id: crypto.randomUUID(),
    reference: exp.field,
    operator: exp.operator,
    value: exp.source?.value ?? '',
    enforced: false,
  }))
}

export const IntegrationMappingFilterBuilder: React.FC<IntegrationMappingFilterBuilderProps> = ({
  serviceId,
  value,
  onChange,
  availableFormFieldDefinitions,
  availableRelationships,
  loading,
  placeholders,
}) => {
  const initialConditions = mapValueToConditions(value)
  const isDataPresent = value && value.length > 0

  const [conditions, setConditions] = useState<FilterCondition[]>(initialConditions)
  const [referenceObject, setReferenceObject] = useState<IntegrationObjectRelationship | null>(null)
  const accountId = useAppSelector(selectAccountId)
  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: referenceObject?.referenceTo || '' },
    },
    { skip: !referenceObject || !accountId },
  )

  const isLoading = useMemo(() => loading || isDescribeFetching, [loading, isDescribeFetching])

  const referenceObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  const [hasInitialized, setHasInitialized] = useState<boolean>(isDataPresent ?? false)

  if (!hasInitialized && value && value.length > 0) {
    setConditions(mapValueToConditions(value))
    setHasInitialized(true)
  }

  const getFormFieldDefinitionByName = useCallback(
    (name: string): FormFieldDefinition | undefined => {
      return availableFormFieldDefinitions.find((field) => field.name === name)
    },
    [availableFormFieldDefinitions],
  )

  const triggerChange = (newConditions: FilterCondition[]) => {
    setConditions(newConditions)

    if (!hasInitialized) {
      setHasInitialized(true)
    }

    const validConditions: FilterCondition[] = newConditions.filter(
      (c) => c.reference && c.operator && c.value !== '' && c.value !== null && c.value !== undefined,
    )

    if (validConditions.length === 0) {
      if (!value || value.length === 0) {
        return
      }
      onChange([])
      return
    }

    const isVariableValue = (value: string) => {
      return FILTER_VALUE_VARIABLES.includes(value)
    }

    const newGroup: Filter = {
      groupOperator: GroupOperator.AND,
      expressions: validConditions.map((c) => ({
        field: c.reference!,
        operator: c.operator!,
        source: { type: isVariableValue(c.value) ? DescriptorType.VARIABLE : DescriptorType.STATIC, value: c.value, valueType: 'string' },
        valueType: getFormFieldDefinitionByName(c.reference ?? '')?.type,
        valueFormat: getFormFieldDefinitionByName(c.reference ?? '')?.extraTypeInfo,
      })),
    }

    onChange([newGroup])
  }

  const filterOptions: SelectOption[] = useMemo(() => {
    return [
      ...availableFormFieldDefinitions.map((field) => {
        return {
          id: field.name,
          label: `${field.label} (${field.name})`,
        }
      }),
      ...(availableRelationships
        ? availableRelationships.map((rel) => {
            return {
              id: rel.relationshipName,
              label: rel.label,
            }
          })
        : []),
      ...referenceObjectFormFieldDefinitions.map((field) => {
        return {
          id: `${referenceObject?.relationshipName}.${field.name}`,
          label: `${referenceObject?.relationshipName}.${field.label} (${referenceObject?.relationshipName}.${field.name})`,
        }
      }),
    ]
  }, [availableFormFieldDefinitions, availableRelationships, referenceObjectFormFieldDefinitions, referenceObject])

  const handleAddCondition = () => {
    const newCondition: FilterCondition = {
      id: crypto.randomUUID(),
      reference: null,
      operator: null,
      value: '',
      enforced: false,
    }
    triggerChange([...conditions, newCondition])
  }

  const handleRemoveCondition = (index: number) => {
    const updated = [...conditions]
    updated.splice(index, 1)
    triggerChange(updated)
  }

  const handleUpdateCondition = (index: number, field: keyof FilterCondition, newValue: string) => {
    const updated = [...conditions]
    const currentCondition = updated[index]

    if (!currentCondition) {
      return
    }

    if (field === 'reference' && currentCondition.reference !== newValue) {
      if (availableRelationships?.some((rel) => rel.relationshipName === newValue)) {
        const relationship = availableRelationships.find((rel) => rel.relationshipName === newValue)

        setReferenceObject(relationship ?? null)
        updated[index] = {
          ...currentCondition,
          reference: null,
          value: '',
        }
      } else {
        updated[index] = {
          ...currentCondition,
          reference: newValue,
          value: '',
        }
      }
    } else {
      updated[index] = {
        ...currentCondition,
        [field]: newValue,
      }
    }

    triggerChange(updated)
  }

  return (
    <Container className='p-0'>
      <Row className='mb-2'>
        <Col className='d-flex align-items-center gap-2'>
          <span className='fw-bold small text-uppercase'>Conditions</span>
          <Tooltip title='Multiple conditions use ‘AND’ logic.' placement='top'>
            <i className='bi bi-info-square cursor-pointer zocks-text-gray-500' style={{ fontSize: '0.9rem' }} />
          </Tooltip>
        </Col>
      </Row>

      <div className='d-flex flex-column gap-2 mb-3'>
        {placeholders.map((condition) => {
          return (
            <ConditionRow
              isEnforced={true}
              key={condition.field}
              selectedLeftValue={condition.field}
              selectedOperator={condition.operator}
              leftValueOptions={condition.field ? [{ id: condition.field, label: condition.field }] : []}
              operatorOptions={condition.operator ? [{ id: condition.operator, label: condition.operator }] : []}
              leftValuePlaceholder='Select field'
              operatorPlaceholder='Select operator'
              rightValueComponent={
                <Form.Control
                  type='text'
                  className='p-2 zocks-bg-color-gray-100 rounded'
                  placeholder='Value'
                  value={condition.source.value || ''}
                  onChange={() => {}}
                  disabled
                />
              }
              onLeftValueChange={() => {}}
              onOperatorChange={() => {}}
              onRemove={() => {}}
            />
          )
        })}
        {conditions.map((condition, index) => {
          return (
            <ConditionRow
              key={condition.id}
              selectedLeftValue={condition.reference}
              selectedOperator={condition.operator}
              leftValueOptions={filterOptions}
              operatorOptions={OPERATOR_OPTIONS.map((op) => ({ id: op.id, label: op.label }))}
              leftValuePlaceholder='Select field'
              operatorPlaceholder='Select operator'
              loading={isLoading}
              rightValueComponent={
                <Form.Control
                  type='text'
                  className='p-2 zocks-bg-color-gray-100 rounded'
                  placeholder='Value'
                  value={condition.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateCondition(index, 'value', e.target.value)}
                  disabled={condition.enforced}
                />
              }
              onLeftValueChange={(id) => handleUpdateCondition(index, 'reference', id)}
              onOperatorChange={(id) => handleUpdateCondition(index, 'operator', id)}
              onRemove={() => handleRemoveCondition(index)}
            />
          )
        })}
      </div>

      <Row>
        <Col>
          <Button type='button' variant='primaryGhost' className='border-0' onClick={handleAddCondition}>
            + Add condition
          </Button>
        </Col>
      </Row>
    </Container>
  )
}
